/**
 * Settings (task 9.8): presentation, save profiles, export, import, reset.
 *
 * Every destructive control names what it destroys before it does it. Import
 * shows a summary of the incoming file and requires a second confirmation,
 * because import replaces everything on the device.
 */
import { useRef, useState, type JSX, type ReactNode } from 'react';
import type { DifficultyProfiles } from '@managing-up/schema';
import { AsyncBoundary } from '../components/AsyncBoundary.js';
import { Button } from '../components/Button.js';
import { Card, CardBody } from '../components/Card.js';
import { CheckboxField, SelectField, TextField } from '../components/Form.js';
import { LaunchLayout, PageHeading } from '../components/LaunchLayout.js';
import { ConfirmDialog, Modal } from '../components/Modal.js';
import { useContentSource } from '../lib/content-context.js';
import { diagnosticFrom, diagnosticText } from '../lib/save/diagnostics.js';
import { exportText, parseImport, type ImportSummary } from '../lib/save/document.js';
import type { SaveProfile } from '../lib/save/types.js';
import {
  MESSAGE_SPACES,
  MESSAGE_SPACE_LABELS,
  resolveMotion,
  type ColourSetting,
  type MessageSpaceSetting,
  type MotionSetting,
  type TextSizeSetting,
  type ThemeSetting,
} from '../lib/settings.js';
import { useAsync } from '../hooks/use-async.js';
import { useActiveProfile, useViewStore } from '../store/view-store.js';

function Section({
  title,
  description,
  children,
  testId,
}: {
  readonly title: string;
  readonly description?: string | undefined;
  readonly children: ReactNode;
  readonly testId: string;
}): JSX.Element {
  return (
    <Card className="mb-4" data-testid={testId}>
      <CardBody>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {description ? <p className="mt-1 text-sm text-ink-muted">{description}</p> : null}
        <div className="mt-4 flex flex-col gap-4">{children}</div>
      </CardBody>
    </Card>
  );
}

/**
 * The Motion and Colour options, and their hints (task 3.6).
 *
 * Exported because they are shell-owned player-facing copy, and every such
 * table goes through `copy-tables.test.ts` at its register class — Instructional
 * here, since each one tells the player what a choice will do (design D8).
 */
export const MOTION_OPTIONS: readonly { readonly value: MotionSetting; readonly label: string }[] =
  [
    { value: 'system', label: 'Match my device' },
    { value: 'full', label: 'Full motion' },
    { value: 'calm', label: 'Calm motion' },
    { value: 'reduced', label: 'Reduced motion' },
  ];

export const COLOUR_OPTIONS: readonly { readonly value: ColourSetting; readonly label: string }[] =
  [
    { value: 'vivid', label: 'Vivid colour' },
    { value: 'plain', label: 'Plain colour' },
  ];

export const MOTION_HINT =
  'Calm motion keeps the movement that tells you something and drops the celebrations. ' +
  'Reduced motion replaces every transition in the game with an instant change.';

export const COLOUR_HINT =
  'Vivid colour gives each person and each channel a colour of their own. ' +
  'Plain colour uses the standard palette everywhere. This does not change motion.';

/**
 * The sample beside the Motion control (design D17, task 3.7).
 *
 * Motion and Colour are the only two settings whose effect cannot be seen on
 * this screen, and a player who has to start a mission to evaluate a setting
 * will not evaluate it.
 *
 * The sample obeys its own value rather than the document's: `data-motion` is
 * written on the wrapper, which is what the suppression rules in `motion.css`
 * key on, so choosing reduced makes the sample go instantly still — and that
 * stillness *is* the demonstration.
 *
 * The chosen value is also the React key, so changing it remounts the burst and
 * replays it. Nothing here holds a timer.
 */
function MotionSample({ setting }: { readonly setting: MotionSetting }): JSX.Element {
  const level = resolveMotion(setting);
  return (
    <div
      aria-hidden="true"
      data-motion={level}
      data-testid="setting-motion-preview"
      className="relative flex h-9 w-24 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-lg border border-line bg-surface-muted"
    >
      <span
        key={setting}
        className="mu-burst absolute h-8 w-8 rounded-full bg-hue-6"
        data-testid="setting-motion-preview-burst"
      />
      <span key={`${setting}-settle`} className="mu-settle h-2 w-8 rounded-full bg-hue-6" />
      <span className="mu-pulse h-2 w-2 rounded-full bg-hue-12" />
    </div>
  );
}

/**
 * The sample beside the Colour control (design D17, task 3.7).
 *
 * Three ring steps, and `data-colour` on the wrapper. Under plain the collapse
 * in `palette.css` redefines every ring token in terms of the semantic palette,
 * and because custom properties inherit, writing the attribute here is enough
 * for the sample to show exactly what the whole application would show.
 */
function ColourSample({ setting }: { readonly setting: ColourSetting }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      data-colour={setting}
      data-testid="setting-colour-preview"
      className="flex h-9 w-24 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-line bg-surface-muted"
    >
      <span className="h-4 w-4 rounded-full bg-hue-1" />
      <span className="h-4 w-4 rounded-full bg-hue-5" />
      <span className="h-4 w-4 rounded-full bg-hue-9" />
    </div>
  );
}

/** A control with its live sample beside it. */
function WithSample({
  children,
  sample,
}: {
  readonly children: ReactNode;
  readonly sample: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-48 flex-1">{children}</div>
      {sample}
    </div>
  );
}

function completionCount(profile: SaveProfile): number {
  return profile.progress.filter((entry) =>
    Object.values(entry.best).some((result) => result.completed),
  ).length;
}

/** Downloads `text` as a file. The client has no server to post it to. */
function downloadText(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * The bug-report payload (task 13.8).
 *
 * Offered only while a run is in progress, because that is the only time there
 * is anything to reproduce. It is deliberately separate from the save export:
 * a save is the player's data and carries their profile names, while this is
 * five identifiers and a list of moves, and the difference matters when
 * somebody is about to paste one of them into a public issue.
 */
function Diagnostics({ profile }: { readonly profile: SaveProfile }): JSX.Element {
  const announce = useViewStore((state) => state.announce);
  const run = profile.run;

  return (
    <Section
      title="Report a problem"
      description="You can reproduce a run from 5 identifiers and the moves you made. The report holds nothing else — no names, no settings, nothing about this device."
      testId="settings-diagnostics"
    >
      {run === null ? (
        <p className="text-sm text-ink-muted" data-testid="diagnostic-none">
          There is no run in progress to report. Start a mission. Reproduce the problem. The report
          will then appear here.
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-muted" data-testid="diagnostic-run">
            {`${run.missionTitle} · ${run.profileLabel} · day ${String(run.day)} · ${String(run.history.length)} move${run.history.length === 1 ? '' : 's'}`}
          </p>
          <div>
            <Button
              data-testid="setting-diagnostic"
              onClick={() => {
                downloadText(
                  diagnosticText(diagnosticFrom(run)),
                  `managing-up-report-${run.packId}-${new Date().toISOString().slice(0, 10)}.json`,
                );
                announce('Diagnostic report saved.');
              }}
            >
              Save a diagnostic report
            </Button>
          </div>
        </>
      )}
    </Section>
  );
}

function Presentation({
  profile,
  profiles,
}: {
  readonly profile: SaveProfile;
  readonly profiles: DifficultyProfiles;
}): JSX.Element {
  const update = useViewStore((state) => state.updateSettings);
  const { settings } = profile;

  return (
    <Section
      title="Play and presentation"
      description="These settings apply immediately. Each save keeps its own."
      testId="settings-presentation"
    >
      <SelectField
        label="Default difficulty"
        hint="This difficulty is selected when you start a mission. You can still change it there."
        value={settings.defaultProfileId}
        data-testid="setting-default-difficulty"
        options={profiles.profiles.map((entry) => ({ value: entry.id, label: entry.label }))}
        onChange={(defaultProfileId) => {
          update({ ...settings, defaultProfileId });
        }}
      />
      <SelectField<ThemeSetting>
        label="Theme"
        value={settings.theme}
        data-testid="setting-theme"
        options={[
          { value: 'system', label: 'Match my device' },
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]}
        onChange={(theme) => {
          update({ ...settings, theme });
        }}
      />
      <SelectField<TextSizeSetting>
        label="Text size"
        value={settings.textSize}
        data-testid="setting-text-size"
        options={[
          { value: 'small', label: 'Small' },
          { value: 'medium', label: 'Medium' },
          { value: 'large', label: 'Large' },
        ]}
        onChange={(textSize) => {
          update({ ...settings, textSize });
        }}
      />
      {/*
        The same stored value the in-mission divider writes (design D6). Two
        controls, one setting: a player who moves the divider mid-mission finds
        this select already showing where they left it.
      */}
      <SelectField<MessageSpaceSetting>
        label="Message space"
        hint="This sets how much of the screen the messages get. The composer takes the rest. You can also move the divider during a mission."
        value={settings.messageSpace}
        data-testid="setting-message-space"
        options={MESSAGE_SPACES.map((size) => ({
          value: size,
          label: MESSAGE_SPACE_LABELS[size],
        }))}
        onChange={(messageSpace) => {
          update({ ...settings, messageSpace });
        }}
      />
      {/*
        Motion and Colour are two settings rather than one, because they answer
        two different accessibility needs (design D7). Changing either one
        leaves the other exactly as it was, and each sits beside a sample that
        demonstrates the value on this screen.
      */}
      <WithSample sample={<MotionSample setting={settings.motion} />}>
        <SelectField<MotionSetting>
          label="Motion"
          hint={MOTION_HINT}
          value={settings.motion}
          data-testid="setting-motion"
          options={[...MOTION_OPTIONS]}
          onChange={(motion) => {
            update({ ...settings, motion });
          }}
        />
      </WithSample>
      <WithSample sample={<ColourSample setting={settings.colour} />}>
        <SelectField<ColourSetting>
          label="Colour"
          hint={COLOUR_HINT}
          value={settings.colour}
          data-testid="setting-colour"
          options={[...COLOUR_OPTIONS]}
          onChange={(colour) => {
            update({ ...settings, colour });
          }}
        />
      </WithSample>
      <CheckboxField
        label="Sound"
        checked={settings.sound}
        testId="setting-sound"
        onChange={(sound) => {
          update({ ...settings, sound });
        }}
      />
    </Section>
  );
}

function Profiles({ active }: { readonly active: SaveProfile }): JSX.Element {
  const doc = useViewStore((state) => state.doc);
  const createProfile = useViewStore((state) => state.createProfile);
  const renameActive = useViewStore((state) => state.renameActiveProfile);
  const selectProfile = useViewStore((state) => state.selectProfile);
  const removeProfile = useViewStore((state) => state.removeProfile);
  const announce = useViewStore((state) => state.announce);

  // A profile name is the player naming themselves, not writing game language:
  // the no-free-text rule belongs to the composer and applies to nothing here.
  const [name, setName] = useState(active.name);
  const [newName, setNewName] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const completions = completionCount(active);

  return (
    <Section
      title="Your saves"
      description="Each save keeps its own progress, Corporate Experience, perks and settings."
      testId="settings-profiles"
    >
      <SelectField
        label="Active save"
        value={active.id}
        data-testid="setting-active-profile"
        options={(doc?.profiles ?? []).map((entry) => ({ value: entry.id, label: entry.name }))}
        onChange={(id) => {
          const chosen = doc?.profiles.find((entry) => entry.id === id);
          selectProfile(id);
          setName(chosen?.name ?? '');
          announce(`Switched to the save ${chosen?.name ?? id}.`);
        }}
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1">
          <TextField
            label="Rename this save"
            value={name}
            testId="setting-rename-input"
            onChange={setName}
          />
        </div>
        <Button
          data-testid="setting-rename"
          disabled={name.trim().length === 0 || name.trim() === active.name}
          onClick={() => {
            const renamed = name.trim();
            renameActive(renamed);
            announce(`Renamed this save to ${renamed}.`);
          }}
        >
          Rename
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1">
          <TextField
            label="New save"
            placeholder="Name"
            value={newName}
            testId="setting-new-profile-input"
            onChange={setNewName}
          />
        </div>
        <Button
          data-testid="setting-new-profile"
          disabled={newName.trim().length === 0}
          onClick={() => {
            const created = newName.trim();
            createProfile(created);
            setName(created);
            setNewName('');
            announce(`Created the save ${created} and switched to it.`);
          }}
        >
          Create
        </Button>
      </div>

      <div>
        <Button
          variant="danger"
          data-testid="setting-delete-profile"
          onClick={() => {
            setConfirmingDelete(true);
          }}
        >
          Delete this save
        </Button>
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete the save ${active.name}?`}
        testId="delete-profile"
        confirmLabel="Delete save"
        consequence={`This deletes the save ${active.name} from this device. You lose ${String(completions)} completed mission${completions === 1 ? '' : 's'}, ${active.career.cxp.toLocaleString()} banked Corporate Experience and every perk it owns${active.run ? ', plus the run in progress' : ''}. This cannot be undone.`}
        onCancel={() => {
          setConfirmingDelete(false);
        }}
        onConfirm={() => {
          const removed = active.name;
          removeProfile(active.id);
          setConfirmingDelete(false);
          announce(`Deleted the save ${removed}.`);
        }}
      />
    </Section>
  );
}

function DataControls(): JSX.Element {
  const doc = useViewStore((state) => state.doc);
  const importSave = useViewStore((state) => state.importSave);
  const resetEverything = useViewStore((state) => state.resetEverything);
  const announce = useViewStore((state) => state.announce);

  const filePicker = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{
    readonly text: string;
    readonly summary: ImportSummary;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const profileCount = doc?.profiles.length ?? 0;

  const download = (): void => {
    if (!doc) return;
    downloadText(exportText(doc), `managing-up-save-${new Date().toISOString().slice(0, 10)}.json`);
    announce('Save data exported.');
  };

  const inspect = async (file: File): Promise<void> => {
    setError(null);
    try {
      // Validated and summarised first. Nothing on the device is touched until
      // the player has seen what is in the file and confirmed the replacement.
      const text = await file.text();
      const { summary } = parseImport(text);
      setPending({ text, summary });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That file could not be read.');
    }
  };

  return (
    <Section
      title="Your data"
      description="Everything is stored on this device. There is no account, and nothing is uploaded."
      testId="settings-data"
    >
      <div className="flex flex-wrap gap-3">
        <Button onClick={download} data-testid="setting-export">
          Export save data
        </Button>
        <Button
          onClick={() => {
            filePicker.current?.click();
          }}
          data-testid="setting-import"
        >
          Import save data
        </Button>
        <input
          ref={filePicker}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          aria-label="Save data file to import"
          data-testid="setting-import-file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void inspect(file);
          }}
        />
      </div>

      {error ? (
        <p role="alert" className="text-sm text-danger" data-testid="import-error">
          {error}
        </p>
      ) : null}

      <div>
        <Button
          variant="danger"
          data-testid="setting-reset"
          onClick={() => {
            setConfirmingReset(true);
          }}
        >
          Reset everything
        </Button>
      </div>

      <Modal
        open={pending !== null}
        title="Replace everything on this device?"
        testId="import-confirm"
        onClose={() => {
          setPending(null);
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setPending(null);
              }}
              data-testid="import-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              data-testid="import-apply"
              onClick={() => {
                if (!pending) return;
                try {
                  const summary = importSave(pending.text);
                  announce(
                    `Imported ${String(summary.profileCount)} save${summary.profileCount === 1 ? '' : 's'}.`,
                  );
                  setPending(null);
                } catch (cause) {
                  setError(cause instanceof Error ? cause.message : 'That file could not be read.');
                  setPending(null);
                }
              }}
            >
              Replace my data
            </Button>
          </>
        }
      >
        <div className="text-sm text-ink" data-testid="import-summary">
          <p>This file contains:</p>
          <ul className="mt-2 list-disc pl-5 text-ink-muted">
            <li>
              {String(pending?.summary.profileCount ?? 0)} save
              {pending?.summary.profileCount === 1 ? '' : 's'} —{' '}
              {pending?.summary.profileNames.join(', ')}
            </li>
            <li>
              {String(pending?.summary.missionsCompleted ?? 0)} completed mission
              {pending?.summary.missionsCompleted === 1 ? '' : 's'}
            </li>
            <li>
              {pending?.summary.runsInProgress === 0
                ? 'No run in progress'
                : `${String(pending?.summary.runsInProgress ?? 0)} run${pending?.summary.runsInProgress === 1 ? '' : 's'} in progress. Each one resumes where you left it.`}
            </li>
            {pending?.summary.migrated === true ? (
              <li data-testid="import-migrated">
                {`This file uses save format ${String(pending.summary.saveVersion)}. Importing updates it to the current version.`}
              </li>
            ) : null}
          </ul>
          <p className="mt-3">
            Importing replaces the {String(profileCount)} save
            {profileCount === 1 ? '' : 's'} on this device now. Export them first if you want to
            keep them.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmingReset}
        title="Reset everything?"
        testId="reset"
        confirmLabel="Erase everything"
        consequence={`This erases all ${String(profileCount)} save${profileCount === 1 ? '' : 's'} on this device. You lose every mission completion and score, all banked Corporate Experience, every perk, every setting, and any run in progress. This cannot be undone.`}
        onCancel={() => {
          setConfirmingReset(false);
        }}
        onConfirm={() => {
          resetEverything();
          setConfirmingReset(false);
          announce('All save data on this device is now erased.');
        }}
      />
    </Section>
  );
}

export function SettingsScreen(): JSX.Element {
  const source = useContentSource();
  const active = useActiveProfile();
  const profiles = useAsync(() => source.profiles(), [source]);

  return (
    <LaunchLayout title="Settings">
      <PageHeading title="Settings" />
      {active ? (
        <>
          <AsyncBoundary result={profiles} label="the difficulty profiles">
            {(value) => <Presentation profile={active} profiles={value} />}
          </AsyncBoundary>
          <Profiles active={active} />
          <DataControls />
          <Diagnostics profile={active} />
        </>
      ) : (
        <p className="text-sm text-ink-muted">Loading your save…</p>
      )}
    </LaunchLayout>
  );
}
